import { useEffect, useRef } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { useLang } from "@/i18n/lang";
import { ORIGIN } from "@/components/Seo";

// Core marketing paths whose DE (default) version lives at the bare URL and
// whose EN version is served under an /en prefix.
const CORE = ["/work", "/services", "/products", "/contact"];

// Localize a DE-canonical internal path for the given language.
// DE (default / x-default) keeps the bare path; EN gets an /en prefix.
// Already-localized (/de/, /en/), legal and external/unknown paths are left as-is.
export function localizePath(path, lang) {
  if (typeof path !== "string") return path;
  if (lang !== "en") return path;
  if (path === "/") return "/en";
  if (path.startsWith("/en/") || path.startsWith("/de/")) return path;
  if (path === "/impressum" || path === "/datenschutz") return path;
  if (CORE.some((p) => path === p || path.startsWith(p + "/"))) return `/en${path}`;
  return path;
}

// hreflang alternates (de / en / x-default) for a core DE-canonical path.
export function coreAlternates(dePath) {
  const de = ORIGIN + dePath;
  const en = ORIGIN + localizePath(dePath, "en");
  return [
    { hrefLang: "de", href: de },
    { hrefLang: "en", href: en },
    { hrefLang: "x-default", href: de },
  ];
}

// <Link> that auto-localizes an internal string `to` to the active language.
export function LocaleLink({ to, ...props }) {
  const { lang } = useLang();
  return <Link to={localizePath(to, lang)} {...props} />;
}

// <NavLink> variant (keeps isActive styling) with the same localization.
export function LocaleNavLink({ to, ...props }) {
  const { lang } = useLang();
  return <NavLink to={localizePath(to, lang)} {...props} />;
}

// Force the page language from the URL locale (deterministic for crawlers) and
// navigate to the counterpart URL ONLY on a real user toggle — never on first
// mount, where the persisted context lang may still hold the previous value.
export function useLocaleRoute(locale, counterpartPath) {
  const { lang, setLang } = useLang();
  const navigate = useNavigate();
  const synced = useRef(false);
  useEffect(() => {
    synced.current = false;
    setLang(locale);
  }, [locale, setLang]);
  useEffect(() => {
    if (lang === locale) {
      synced.current = true;
      return;
    }
    if (synced.current) navigate(counterpartPath);
  }, [lang, locale, counterpartPath, navigate]);
}
